import { NavLink } from 'react-router-dom'
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { cn } from '@/internationalization/cn'
import { APP_NAME } from '@/internationalization/constants'
import { useT } from '@/internationalization/i18n'
import { visibleSections } from '@/app/navigation'
import { useSessionStore, useUiStore } from '@/stores'
import { IconButton, Tooltip } from '@/components/ui'
import { LogoMark } from '@/components/brand'

/**
 * Persistent primary navigation, shown at lg and above.
 *
 * Below lg it is not rendered at all — MobileNav renders the same tree inside a
 * Drawer. One component that is sometimes a sidebar and sometimes a drawer ends
 * up with the worst of both, so they are separate and share NAV_SECTIONS.
 *
 * Collapsed mode keeps the icons and moves labels into tooltips. The collapse
 * state is persisted, because a user who collapses it wants it collapsed
 * tomorrow.
 */
export function Sidebar({
  onNavigate,
  /** Set by MobileNav: inside a drawer the collapsed state makes no sense. */
  forceExpanded = false,
}: {
  onNavigate?: () => void
  forceExpanded?: boolean
}) {
  const t = useT()
  const permissions = useSessionStore((s) => s.permissions)
  const canAccessVocabulary = useSessionStore((s) => s.canAccessVocabulary)
  const canAccessFeedbackInbox = useSessionStore((s) => s.canAccessFeedbackInbox)
  const collapsedPreference = useUiStore((s) => s.isSidebarCollapsed)
  const toggleSidebar = useUiStore((s) => s.toggleSidebar)
  const isCollapsed = collapsedPreference && !forceExpanded
  const sections = visibleSections(permissions, canAccessVocabulary, canAccessFeedbackInbox)

  return (
    <nav
      aria-label={t('nav.primary')}
      className={cn(
        'flex h-full flex-col bg-surface',
        !forceExpanded && 'border-r border-border',
        forceExpanded ? 'w-full' : isCollapsed ? 'w-sidebar-collapsed' : 'w-sidebar',
      )}
    >
      <div className="flex h-topbar items-center gap-2 border-b border-border px-3">
        <LogoMark className="h-7 w-7" />
        {!isCollapsed && <span className="truncate text-md font-semibold text-fg">{APP_NAME}</span>}
        <span className={forceExpanded ? 'hidden' : 'ml-auto hidden lg:block'}>
          <IconButton
            label={isCollapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
            icon={isCollapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
            size="sm"
            onClick={toggleSidebar}
          />
        </span>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 py-3">
        {sections.map((section) => (
          <div key={section.id} className="mb-4 last:mb-0">
            {section.label && !isCollapsed && (
              <p className="px-2 pb-1.5 text-2xs font-semibold uppercase tracking-wide text-fg-subtle">
                {t(`nav.section.${section.id}`)}
              </p>
            )}
            <ul className="space-y-0.5">
              {section.items.map((item) => {
                const label = t(`nav.item.${item.id}`)
                const link = (
                  <NavLink
                    to={item.to}
                    end={item.to === '/'}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      cn(
                        'flex min-h-10 items-center gap-2.5 rounded-md px-2 text-base font-medium',
                        'transition-colors duration-fast',
                        isCollapsed && 'justify-center',
                        isActive
                          ? 'bg-brand-subtle text-brand'
                          : 'text-fg-muted hover:bg-surface-muted hover:text-fg',
                      )
                    }
                  >
                    <item.icon aria-hidden className="h-[18px] w-[18px] shrink-0" />
                    {!isCollapsed && <span className="truncate">{label}</span>}
                  </NavLink>
                )
                return (
                  <li key={item.id}>
                    {isCollapsed ? (
                      <Tooltip content={label} side="right">
                        {link}
                      </Tooltip>
                    ) : (
                      link
                    )}
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </div>
    </nav>
  )
}
